// Cross-linking data: Tools ↔ Insights relationships for SEO Authority Loop

export interface ToolInsightMapping {
  toolId: string;
  relatedInsights: string[]; // insight slugs
}

export interface InsightToolMapping {
  insightSlug: string;
  relatedTools: string[]; // tool IDs
}

// Map each tool to related insights
export const toolToInsights: Record<string, string[]> = {
  // Authentication & Deliverability Tools
  "email-checker": [
    "summer-email-drop",
    "december-email-drop",
    "cold-email-deliverability",
    "domain-warmup-strategies",
  ],
  "auth-checker": [
    "spf-dkim-dmarc-guide",
    "domain-warmup-strategies",
    "cold-email-deliverability",
  ],
  "blacklist-checker": [
    "summer-email-drop",
    "december-email-drop",
    "domain-warmup-strategies",
    "cold-email-deliverability",
  ],
  "header-analyzer": [
    "spf-dkim-dmarc-guide",
    "sending-time-spam-filters",
    "phishing-attack-detection",
  ],
  "tab-predictor": [
    "gmail-promotions-tab",
    "sending-time-spam-filters",
    "cold-email-deliverability",
  ],
  "content-analyzer": [
    "cold-email-deliverability",
    "gmail-promotions-tab",
    "sending-time-spam-filters",
  ],
  "email-preview": [
    "cold-email-deliverability",
    "gmail-promotions-tab",
  ],
  "subject-analyzer": [
    "cold-email-deliverability",
    "sending-time-spam-filters",
    "gmail-promotions-tab",
  ],
  "bulk-subject-analyzer": [
    "cold-email-deliverability",
    "gmail-promotions-tab",
  ],
  "domain-age-checker": [
    "domain-warmup-strategies",
    "cold-email-deliverability",
  ],
  "bounce-rate-calculator": [
    "summer-email-drop",
    "cold-email-deliverability",
    "domain-warmup-strategies",
    "december-email-drop",
  ],
  "dmarc-analyzer": [
    "spf-dkim-dmarc-guide",
    "phishing-attack-detection",
  ],
  "m365-tenant-analyzer": [
    "spf-dkim-dmarc-guide",
    "phishing-attack-detection",
  ],
  "dns-propagation-checker": [
    "spf-dkim-dmarc-guide",
    "domain-warmup-strategies",
  ],
  
  // SEO Tools
  "seo-meta-analyzer": [
    "vibe-coding-guide",
    "no-code-vs-code",
  ],
  "robots-checker": [
    "vibe-coding-guide",
    "no-code-vs-code",
  ],
  
  // Security Tools
  "phishing-detector": [
    "phishing-attack-detection",
    "secure-coding-practices",
    "api-security-best-practices",
  ],
  "link-validator": [
    "phishing-attack-detection",
    "secure-coding-practices",
  ],
  
  // Productivity Tools
  "pm-cost-calculator": [
    "project-management-tools",
    "no-code-vs-code",
    "it-stakeholder-roles",
    "startup-failure-delegation",
    "departmental-autonomy",
  ],
  "meeting-cost-calculator": [
    "project-management-tools",
    "ai-agents-automation",
    "it-stakeholder-roles",
    "startup-failure-delegation",
    "departmental-autonomy",
  ],
  "collaboration-comparison": [
    "project-management-tools",
    "no-code-vs-code",
    "it-stakeholder-roles",
    "startup-failure-delegation",
    "departmental-autonomy",
  ],
  
  // Developer Tools
  "global-latency-tester": [
    "api-security-best-practices",
    "llm-integration-guide",
  ],
  "webhook-debugger": [
    "api-security-best-practices",
    "llm-integration-guide",
    "ai-agents-automation",
  ],
  "api-response-simulator": [
    "api-security-best-practices",
    "llm-integration-guide",
    "vibe-coding-guide",
  ],
};

// Map each insight to related tools
export const insightToTools: Record<string, string[]> = {
  // Email Deliverability Insights
  "summer-email-drop": [
    "email-checker",
    "blacklist-checker",
    "auth-checker",
  ],
  "december-email-drop": [
    "email-checker",
    "blacklist-checker",
    "auth-checker",
  ],
  "sending-time-spam-filters": [
    "tab-predictor",
    "header-analyzer",
    "subject-analyzer",
  ],
  "domain-warmup-strategies": [
    "auth-checker",
    "blacklist-checker",
    "domain-age-checker",
    "dns-propagation-checker",
  ],
  "spf-dkim-dmarc-guide": [
    "auth-checker",
    "dmarc-analyzer",
    "header-analyzer",
    "dns-propagation-checker",
  ],
  "cold-email-deliverability": [
    "email-checker",
    "subject-analyzer",
    "blacklist-checker",
    "domain-age-checker",
    "bounce-rate-calculator",
  ],
  "gmail-promotions-tab": [
    "tab-predictor",
    "subject-analyzer",
    "content-analyzer",
  ],
  
  // Cybersecurity Insights
  "phishing-attack-detection": [
    "phishing-detector",
    "link-validator",
    "header-analyzer",
  ],
  "secure-coding-practices": [
    "phishing-detector",
    "link-validator",
    "api-response-simulator",
  ],
  "api-security-best-practices": [
    "webhook-debugger",
    "api-response-simulator",
    "global-latency-tester",
  ],
  
  // AI Development Insights
  "vibe-coding-guide": [
    "api-response-simulator",
    "webhook-debugger",
    "seo-meta-analyzer",
  ],
  "ai-coding-assistants": [
    "api-response-simulator",
    "webhook-debugger",
  ],
  "prompt-engineering-developers": [
    "api-response-simulator",
    "subject-analyzer",
  ],
  "no-code-vs-code": [
    "pm-cost-calculator",
    "collaboration-comparison",
    "seo-meta-analyzer",
  ],
  "ai-agents-automation": [
    "webhook-debugger",
    "api-response-simulator",
    "meeting-cost-calculator",
  ],
  "llm-integration-guide": [
    "api-response-simulator",
    "webhook-debugger",
    "global-latency-tester",
  ],
  
  // Productivity Insights
  "project-management-tools": [
    "pm-cost-calculator",
    "meeting-cost-calculator",
    "collaboration-comparison",
  ],
  
  // New 2026 Insights
  "reply-based-deliverability": [
    "email-checker",
    "subject-analyzer",
    "tab-predictor",
  ],
  "zero-trust-email-dmarc": [
    "auth-checker",
    "dmarc-analyzer",
    "dns-propagation-checker",
  ],
  "geo-vs-seo": [
    "seo-meta-analyzer",
    "robots-checker",
    "api-response-simulator",
  ],
  
  // Business IT Insights
  "it-stakeholder-roles": [
    "pm-cost-calculator",
    "meeting-cost-calculator",
    "collaboration-comparison",
  ],
  "startup-failure-delegation": [
    "pm-cost-calculator",
    "meeting-cost-calculator",
    "collaboration-comparison",
  ],
  "departmental-autonomy": [
    "pm-cost-calculator",
    "meeting-cost-calculator",
    "collaboration-comparison",
  ],
  "upwork-fiverr-decline": [
    "pm-cost-calculator",
    "meeting-cost-calculator",
    "collaboration-comparison",
  ],
  "microsoft-drops-claude-code": [
    "pm-cost-calculator",
    "meeting-cost-calculator",
    "collaboration-comparison",
  ],
  "ai-cost-vs-employees": [
    "pm-cost-calculator",
    "meeting-cost-calculator",
    "collaboration-comparison",
  ],
};

// Helper functions
export const getRelatedInsightsForTool = (toolId: string): string[] => {
  return toolToInsights[toolId] || [];
};

export const getRelatedToolsForInsight = (insightSlug: string): string[] => {
  return insightToTools[insightSlug] || [];
};
